import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Mail, Plus } from 'lucide-react'
import { Badge } from '~/components/ui/badge'
import type { BadgeTone } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import {
    getDashboard,
    listAlerts,
    updateAlert,
    updateNotifySettings,
} from '~/api/monitoring'
import type { MonitoringAlert } from '~/api/types'
import { useAuthStore } from '~/stores/auth-store'

const TYPE_LABEL: Record<string, string> = {
    deviation: '기준 이탈',
    near: '근접 경고',
    notice: '공지',
}
const SEVERITY: Record<string, { label: string; tone: BadgeTone }> = {
    high: { label: '높음', tone: 'danger' },
    medium: { label: '중간', tone: 'warning' },
    low: { label: '낮음', tone: 'neutral' },
}
const STATUS_LABEL: Record<string, string> = {
    unread: '미확인',
    ack: '확인',
    resolved: '조치완료',
}

/** 알림센터 — 기준 이탈·근접 알림 목록 + 상태 변경. */
export function AlertsSection({ dashboardId }: { dashboardId: string }) {
    const navigate = useNavigate()
    const [alerts, setAlerts] = useState<MonitoringAlert[] | null>(null)
    const [busyId, setBusyId] = useState<string | null>(null)
    // 알림 채널 설정
    const [email, setEmail] = useState('')
    const [emailOn, setEmailOn] = useState(false)
    const [savingCfg, setSavingCfg] = useState(false)
    const [cfgMsg, setCfgMsg] = useState<string | null>(null)

    const load = useCallback(() => {
        listAlerts(dashboardId)
            .then(setAlerts)
            .catch(() => setAlerts([]))
    }, [dashboardId])

    useEffect(() => {
        load()
        getDashboard(dashboardId)
            .then((d) => {
                // 저장된 수신처가 없으면 로그인 사용자 이메일(knoka claim)로 기본값을 채운다.
                const fallback = useAuthStore.getState().user?.email ?? ''
                setEmail(d.notifyEmail || fallback)
                setEmailOn(Boolean(d.notifyEmailOn))
            })
            .catch(() => undefined)
    }, [load, dashboardId])

    const saveCfg = async () => {
        setSavingCfg(true)
        setCfgMsg(null)
        try {
            await updateNotifySettings(dashboardId, {
                notifyEmail: email || null,
                notifyEmailOn: emailOn,
            })
            setCfgMsg('알림 설정을 저장했어요.')
        } catch {
            setCfgMsg('저장에 실패했어요.')
        } finally {
            setSavingCfg(false)
        }
    }

    const changeStatus = async (alertId: string, status: string) => {
        setBusyId(alertId)
        try {
            await updateAlert(dashboardId, alertId, status)
            load()
        } finally {
            setBusyId(null)
        }
    }

    if (alerts === null) {
        return <p className='text-muted-foreground text-sm'>불러오는 중…</p>
    }

    const unresolved = alerts.filter((a) => a.status !== 'resolved').length

    return (
        <div className='grid gap-4'>
            <section className='border-border bg-card rounded-md border p-5'>
                <h2 className='text-foreground text-sm font-semibold'>
                    알림 채널 설정
                </h2>
                <p className='text-muted-foreground mt-0.5 mb-3 text-xs'>
                    기준 이탈·근접 발생 시 받을 채널을 설정하세요. 인앱 알림은
                    기본 제공됩니다.
                </p>
                <div className='grid gap-3'>
                    <div className='border-border flex items-start gap-3 rounded-md border p-3'>
                        <Mail className='text-primary mt-1 size-4 shrink-0' />
                        <div className='flex-1'>
                            <label className='text-foreground flex items-center justify-between text-sm font-medium'>
                                이메일
                                <input
                                    type='checkbox'
                                    checked={emailOn}
                                    onChange={(e) =>
                                        setEmailOn(e.target.checked)
                                    }
                                />
                            </label>
                            <input
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder='받을 이메일 주소'
                                className='border-border bg-card text-foreground mt-2 w-full rounded-md border px-2.5 py-1.5 text-sm'
                            />
                        </div>
                    </div>
                </div>
                <div className='mt-3 flex items-center gap-2'>
                    <Button onClick={saveCfg} disabled={savingCfg}>
                        설정 저장
                    </Button>
                    {cfgMsg ? (
                        <span className='text-muted-foreground text-xs'>
                            {cfgMsg}
                        </span>
                    ) : null}
                </div>
            </section>

            <section className='border-border bg-card rounded-md border p-5'>
                <div className='mb-3 flex items-center gap-2'>
                    <Bell className='text-primary size-4' />
                    <h2 className='text-foreground text-sm font-semibold'>
                        알림
                    </h2>
                    {unresolved > 0 ? (
                        <Badge tone='danger'>미해결 {unresolved}</Badge>
                    ) : (
                        <Badge tone='success'>미해결 없음</Badge>
                    )}
                </div>
                <p className='text-muted-foreground mb-3 text-xs'>
                    측정값을 올리면 기준 이탈·근접 시 알림이 자동 생성됩니다.
                    이메일 채널로 알림을 받습니다.
                </p>

                {alerts.length === 0 ? (
                    <div className='flex flex-wrap items-center gap-3'>
                        <p className='text-muted-foreground text-sm'>
                            아직 알림이 없어요. 데이터를 올려 점검을 시작하세요.
                        </p>
                        <Button
                            size='sm'
                            onClick={() =>
                                navigate(`/monitoring/${dashboardId}/settings`)
                            }>
                            <Plus className='size-4' />
                            데이터 적재
                        </Button>
                    </div>
                ) : (
                    <div className='overflow-x-auto'>
                        <table className='w-full text-sm'>
                            <thead>
                                <tr className='text-muted-foreground border-border border-b text-xs'>
                                    <th className='py-2 pr-3 text-left'>
                                        시각
                                    </th>
                                    <th className='py-2 pr-3 text-left'>
                                        유형
                                    </th>
                                    <th className='py-2 pr-3 text-left'>
                                        대상
                                    </th>
                                    <th className='py-2 pr-3 text-left'>
                                        심각도
                                    </th>
                                    <th className='py-2 pr-3 text-left'>
                                        상태
                                    </th>
                                    <th className='py-2 text-left'>조치</th>
                                </tr>
                            </thead>
                            <tbody>
                                {alerts.map((a) => {
                                    const sev =
                                        SEVERITY[a.severity] ?? SEVERITY.low
                                    return (
                                        <tr
                                            key={a.id}
                                            className='border-border border-b align-top last:border-0'>
                                            <td className='text-muted-foreground py-2 pr-3 text-xs'>
                                                {a.createdAt?.slice(0, 16) ??
                                                    '—'}
                                            </td>
                                            <td className='py-2 pr-3 text-xs'>
                                                {TYPE_LABEL[a.alertType] ??
                                                    a.alertType}
                                            </td>
                                            <td className='text-foreground py-2 pr-3'>
                                                {a.title}
                                                {a.message ? (
                                                    <span className='text-muted-foreground block text-xs'>
                                                        {a.message}
                                                    </span>
                                                ) : null}
                                            </td>
                                            <td className='py-2 pr-3'>
                                                <Badge tone={sev.tone}>
                                                    {sev.label}
                                                </Badge>
                                            </td>
                                            <td className='text-muted-foreground py-2 pr-3 text-xs'>
                                                {STATUS_LABEL[a.status] ??
                                                    a.status}
                                            </td>
                                            <td className='py-2'>
                                                <div className='flex gap-1'>
                                                    {a.status === 'unread' ? (
                                                        <Button
                                                            variant='outline'
                                                            size='sm'
                                                            disabled={
                                                                busyId === a.id
                                                            }
                                                            onClick={() =>
                                                                changeStatus(
                                                                    a.id,
                                                                    'ack'
                                                                )
                                                            }>
                                                            확인
                                                        </Button>
                                                    ) : null}
                                                    {a.status !== 'resolved' ? (
                                                        <Button
                                                            variant='outline'
                                                            size='sm'
                                                            disabled={
                                                                busyId === a.id
                                                            }
                                                            onClick={() =>
                                                                changeStatus(
                                                                    a.id,
                                                                    'resolved'
                                                                )
                                                            }>
                                                            조치완료
                                                        </Button>
                                                    ) : (
                                                        <span className='text-success text-xs'>
                                                            ✓ 완료
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    )
}
